<template>
  <InputLabelsWrapper :state-error>
    <!-- Pass label slots to InputLabelsWrapper slots of the same name -->
    <template v-for="name in ['label', 'helperText']" #[name]="slotProps">
      <slot :name="name" v-bind="slotProps" />
    </template>

    <FilterMenuComponent
      ref="filterMenuRef"
      v-model="model"
      :items="filterItems"
      :search-string-callback="(item) => item.value.name"
      :state-error
      :shaded-overlay="highlightMenu"
      :small
      :disabled
      :all-open
      @confirmed="onFilterConfirmed"
      @cancelled="onFilterCancelled"
    >
      <!-- Add extra top-level portfolio item at the top -->
      <template #[`POR_${filterStore.currentPortfolio?.displayName}`]>
        <div class="flex items-center gap-1.5">
          <BaseBadge v-if="filterStore.currentPortfolio.demo" color="gray">
            Demo
          </BaseBadge>
          <BaseIcon name="briefcase-4" filled class="text-lg" />
          <span>{{ filterStore.currentPortfolio.displayName }}</span>
        </div>
      </template>
      <!-- Undo allocation point label prefix -->
      <template
        v-for="allocationPoint in resolvedAllocationPoints"
        :key="allocationPoint.id"
        #[`AP_${allocationPoint.displayName}`]
      >
        <div class="flex items-center gap-1.5">
          <BaseBadge v-if="allocationPoint.demo" color="gray"> Demo </BaseBadge>
          <span>{{ allocationPoint.displayName }}</span>
        </div>
      </template>
      <!-- Enrich asset names with status and icon -->
      <template
        v-for="asset in resolvedAssets"
        :key="asset.id"
        #[asset.displayName]
      >
        <div class="flex items-center gap-1.5">
          <StatusIndicatorComponent :asset />
          <BaseBadge v-if="asset.demo" color="gray"> Demo </BaseBadge>
          <span
            class="truncate"
            :class="{
              'text-gray-300':
                allowedAssetTypes && !allowedAssetTypes.includes(asset.type),
            }"
            >{{ asset.displayName }}</span
          >
        </div>
      </template>
    </FilterMenuComponent>
  </InputLabelsWrapper>
</template>

<script setup>
import { ref, computed } from "vue"
import { useRouter } from "vue-router"
import { triggerEvent } from "@/services/plausibleService.js"
import useFilterStore from "@/stores/filterStore.js"
import FilterMenuComponent from "@/components/input/FilterMenuComponent.vue"
import StatusIndicatorComponent from "@/components/information/StatusIndicatorComponent.vue"
import {
  BaseIcon,
  BaseBadge,
  InputLabelsWrapper,
} from "@repowerednl/ui-component-library"

const model = defineModel({ type: Object })

const props = defineProps({
  /**
   * Override the allocation points to show in the filter menu. If not provided,
   * the current portfolio's allocation points will be used.
   */
  allocationPoints: {
    type: Array,
    default: null,
  },
  /**
   * Override the assets to show in the filter menu. If not provided, the
   * current portfolio's assets will be used.
   */
  assets: {
    type: Array,
    default: null,
  },
  /**
   * Disable the allocation points in the filter menu.
   */
  noAllocationPoints: Boolean,
  /**
   * Disable the assets in the filter.
   */
  noAssets: Boolean,
  /**
   * Show the current portfolio among the options.
   */
  withPortfolio: Boolean,
  /**
   * Limit the selectable asset types. If not provided or false, all asset types
   * are allowed.
   */
  allowedAssetTypes: {
    type: [Array, Boolean],
    default: false,
  },
  /**
   * Show an error state in the menu.
   */
  stateError: Boolean,
  /**
   * Apply dark borders to the menu.
   */
  darkBorders: Boolean,
  /**
   * Render the menu in a smaller size, suitable for compact layouts.
   */
  small: Boolean,
  /**
   * Start with all allocation points unfolded.
   */
  allOpen: Boolean,
  /**
   * Disable the picker's button and close the menu if it's open.
   */
  disabled: Boolean,
})

const router = useRouter()
const filterStore = useFilterStore()
const filterMenuRef = ref()
const highlightMenu = ref(false)

const resolvedAllocationPoints = computed(
  () => props.allocationPoints ?? filterStore.currentPortfolioAllocationPoints,
)

const resolvedAssets = computed(
  () => props.assets ?? filterStore.currentPortfolioAssets,
)

const filterItems = computed(() => {
  // Prefix allocation point labels to distinguish from assets with the same name
  const allocationPointItems = resolvedAllocationPoints.value.map(
    (allocationPoint) => {
      return {
        label: `AP_${allocationPoint.displayName}`,
        value: allocationPoint,
        disabled: props.noAllocationPoints,
      }
    },
  )
  const assetItems = resolvedAssets.value.map((asset) => {
    const allocationPoint = resolvedAllocationPoints.value.find(
      (ap) => ap.id === asset.allocationPointId,
    )
    return {
      label: asset.displayName,
      value: asset,
      disabled:
        props.noAssets ||
        (props.allowedAssetTypes &&
          !props.allowedAssetTypes.includes(asset.type)),
      parent: allocationPoint ? `AP_${allocationPoint.displayName}` : null,
    }
  })
  if (props.withPortfolio && filterStore.currentPortfolio) {
    return [
      {
        label: `POR_${filterStore.currentPortfolio.displayName}`,
        value: filterStore.currentPortfolio,
      },
      ...allocationPointItems,
      ...assetItems,
    ]
  } else {
    return [...allocationPointItems, ...assetItems]
  }
})

function onFilterCancelled() {
  router.replace({ query: null })
  highlightMenu.value = false
}

function onFilterConfirmed() {
  triggerEvent("toolbar:metering-point-changed")
  onFilterCancelled()
}

function openMenu(highlight = false) {
  filterMenuRef.value.openMenu()
  highlightMenu.value = highlight
}

defineExpose({
  openMenu,
})
</script>
